/**
 * Operator/support-facing labels for SFTP auth mode (Story 3.5a).
 * Matches `SFTP_AUTH_TYPE_FORM_OPTIONS` naming — do not invent parallel vocabulary.
 */

import { SFTP_AUTH_TYPE, effectiveSftpAuthType, type SftpAuthType } from "../constants/sftp-auth-type";
import { sftpAuthTypeForLeg, type SftpAuthStepSlice } from "./workflow-file-location-secret";

/** Same strings as the SFTP auth dropdown (`Password` / `SSH private key`). */
export function sftpAuthModeDisplayLabel(mode: SftpAuthType): string {
	return mode === SFTP_AUTH_TYPE.SshKey ? "SSH private key" : "Password";
}

/** Resolve raw AuthType fields to a display label after LD/feature-gate effective mapping. */
export function sftpAuthModeLabelFromRaw(authType: string | undefined | null): string {
	return sftpAuthModeDisplayLabel(effectiveSftpAuthType(authType));
}

/** Discriminated result for view-only auth summary — drives tests and `AddWorkflowStepPopup` rendering. */
export type ViewOnlyAuthModeSummary =
	| { kind: "hidden" }
	| { kind: "sftp-filecopy"; sourceLabel: string; destinationLabel: string }
	| { kind: "sftp-single"; authLabel: string }
	| { kind: "ftps"; authLabel: "Password" };

/**
 * Pure logic for Story 3.5a view-only authentication lines (support / inspect).
 * Per-leg labels use `sftpAuthTypeForLeg` (same as save/API). Shown even when `useTemplateVariables`
 * is true so mode is visible without exposing secrets.
 */
export function computeViewOnlyAuthModeSummary(opts: {
	isViewOnly: boolean;
	/** Same semantics as `AddWorkflowStepPopup.isSftpStorageEndpointSelected()`. */
	isSftpEndpoint: boolean;
	fileCopy: boolean;
	isFtps: boolean;
	formData: SftpAuthStepSlice | Record<string, unknown>;
}): ViewOnlyAuthModeSummary {
	if (!opts.isViewOnly) {
		return { kind: "hidden" };
	}
	const fd = opts.formData as SftpAuthStepSlice;
	if (opts.isSftpEndpoint) {
		if (opts.fileCopy) {
			return {
				kind: "sftp-filecopy",
				sourceLabel: sftpAuthModeDisplayLabel(sftpAuthTypeForLeg(fd, "source")),
				destinationLabel: sftpAuthModeDisplayLabel(sftpAuthTypeForLeg(fd, "destination")),
			};
		}
		return {
			kind: "sftp-single",
			authLabel: sftpAuthModeLabelFromRaw(fd.AuthType ?? fd.authType),
		};
	}
	if (opts.isFtps) {
		return { kind: "ftps", authLabel: "Password" };
	}
	return { kind: "hidden" };
}
